import MinHeap from 'heap-js';
import { Coordinate, PlannedRouteTrail, Trail } from "atlasguides-web-common/src/functions/guides/dto/guide-geo.dto";
import { TrailPoint } from "../RouteTree";
import { GuideGeoExt } from "commonTypes/GuideUse/guideGeo";

type Graph = Map<string, { coord: Coordinate, neighbors: Map<string, number> }>;

const STEP_THRESHOLD_METERS = 100;

export class DijkstraPath {

    private guideData:GuideGeoExt;
    private graph:Graph;

    constructor(guide: GuideGeoExt) {
        this.guideData = guide;
        this.graph = this.buildGraph(this.guideData.trails);
    }

    public getDijkstraPathForLocations(pins: number[][], guideData: GuideGeoExt, newTrail: PlannedRouteTrail): Coordinate[] {
        const routeTree = this.guideData.routeTree;
        if (guideData.trailLength === 0 || routeTree == null){
            return [];
        }

        let path: Coordinate[] = [];
        const numPins = pins.length;

        let startPinPoint;
        let endPinPoint;

        // Search
        for (let z = 0; z < numPins - 1; z++) {

            // get the start and end point for this run of the algorithm
            let thisPin = pins[z];
            let nextPin = pins[z + 1];
            
            const thisPinPoint = routeTree.getNearestPoint(thisPin[0], thisPin[1]);
            const nextPinPoint = routeTree.getNearestPoint(nextPin[0], nextPin[1]);

            if (thisPinPoint == null || nextPinPoint == null) {
                return [];
            }

            if (z === 0){
                startPinPoint = thisPinPoint;
            }
            if (z === numPins - 2){
                endPinPoint = nextPinPoint;
            }

            const startKey = this.coordKeyTrailPoint(thisPinPoint);
            const endKey = this.coordKeyTrailPoint(nextPinPoint);

            const sectionPath:Coordinate[] = this.dijkstra(this.graph, startKey, endKey);
            if (sectionPath != null) {
                // Get copy and append to the path
                const sectionPathCopy = sectionPath.map(coord => [...coord ] as Coordinate);
                path.push(...sectionPathCopy);
            }
        }

        if (startPinPoint?.distance && endPinPoint?.distance) {
            newTrail.trackLength = Math.abs(endPinPoint?.distance - startPinPoint?.distance);
        }

        // Fix point distances
        const numPoints = path.length;

        const startPointDistance = path[0][3];
        const endPointDistance = path[numPoints - 1][3];
        const reverse = (startPointDistance > endPointDistance);
        for (let i = 0; i < numPoints; i++) {
            const point = path[i];
            const distance = point[3];
            if (!reverse){
                point[3] = distance - startPointDistance;
            }
            else{
                point[3] = startPointDistance - distance;
            }
        }

        // Calculate total ascent and descent 
        let prevAlt = null;
        let prevDistance = null;
        let totalAscent = 0;
        let totalDescent = 0;

        for (let i = 0; i < numPoints; i++) {
            const point = path[i];
            const alt = point[2];
            const distance = point[3];

            if (prevAlt == null){
                prevAlt = alt;
                prevDistance = distance;
            }
            else if ((prevDistance !== null && Math.abs(distance - prevDistance) >= STEP_THRESHOLD_METERS) || (i === numPoints - 1))
            {
                if (alt > prevAlt) {
                    totalAscent += (alt - prevAlt);
                }
                else if (alt < prevAlt) {
                    totalDescent += (prevAlt - alt);
                }

                prevAlt = alt;
                prevDistance = distance;
            }
        }

        if (totalAscent > 0 || totalDescent > 0) {
            newTrail.totalAscent = totalAscent;
            newTrail.totalDescent = totalDescent;
        }

        return path;
    }

    private coordKey([lon, lat]: Coordinate): string {
        return `${lat},${lon}`;
    }
    private coordKeyTrailPoint(point:TrailPoint): string {
        return `${point.latitude},${point.longitude}`;
    }

    private haversineDistance([lon1, lat1]: Coordinate, [lon2, lat2]: Coordinate): number {
        const R = 6371000; // meters
        const toRad = (d: number) => d * Math.PI / 180;
        const phi1 = toRad(lat1), phi2 = toRad(lat2);
        const deltaPhi = toRad(lat2 - lat1);
        const deltaLambda = toRad(lon2 - lon1);

        const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
              Math.cos(phi1) * Math.cos(phi2) *
              Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    private buildGraph(trails: Trail[]): Graph {
        const graph: Graph = new Map();

        for (const trail of trails) {
            const coords = trail.geometry.coordinates[0];

            for (let i = 0; i < coords.length - 1; i++) {
                const a = coords[i], b = coords[i + 1];
                const dist = this.haversineDistance(a, b);
                const keyA = this.coordKey(a), keyB = this.coordKey(b);

                if (!graph.has(keyA)) graph.set(keyA, { coord: a, neighbors: new Map() });
                if (!graph.has(keyB)) graph.set(keyB, { coord: b, neighbors: new Map() });

                graph.get(keyA)!.neighbors.set(keyB, dist);
                graph.get(keyB)!.neighbors.set(keyA, dist);
            }
        }

        return graph;
    }    

    private dijkstra(graph: Graph, startKey: string, endKey: string): Coordinate[] {
        const distances: Map<string, number> = new Map();
        const prev: Map<string, string | null> = new Map();
        const visited: Set<string> = new Set();

        const queue = new MinHeap<[number, string]>((a: [number, string], b: [number, string]) => a[0] - b[0]);

        for (const key of graph.keys()) {
            distances.set(key, Infinity);
            prev.set(key, null);
        }

        distances.set(startKey, 0);
        queue.push([0, startKey]);

        while (!queue.isEmpty()) {
            const [currentDist, currentKey] = queue.pop()!;

            if (visited.has(currentKey)) continue;
            visited.add(currentKey);

            if (currentKey === endKey) break;

            const currentNode = graph.get(currentKey)!;

            for (const [neighborKey, weight] of currentNode.neighbors) {
                if (visited.has(neighborKey)) continue;

                const alt = currentDist + weight;
                if (alt < distances.get(neighborKey)!) {
                    distances.set(neighborKey, alt);
                    prev.set(neighborKey, currentKey);
                    queue.push([alt, neighborKey]);
                }
            }
        }

        // Reconstruct path
        const path: Coordinate[] = [];
        let curr = endKey;
        while (curr) {
            path.unshift(graph.get(curr)!.coord);
            curr = prev.get(curr)!;
        }

        return path;
    }
}